// The circular fan dial (SVG) â€” self-contained control, no page chrome.
//
// 6 coloured wedges = the 6 firmware-valid speeds [10,20,40,60,80,100]
// (SP1..FULL); the centre circle is On/Off. Command `1-<speed>` (`1-0` = off),
// matching the switching firmware. Status `mode-speed` (speed 0 = off).
//
// Used both inline on the room-grid fan card and on the full device screen, so
// it seeds its own last-known status from the DB when an initial one isn't
// passed in (the inline card doesn't pre-fetch).

import React, { useEffect, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { useApp } from '../../../core/state/AppContext';
import { useDeviceStatus } from '../../../core/mqtt/useDeviceStatus';
import { parseStatus } from '../../../core/mqtt/mqttClient';
import { publishCommand } from '../../../core/utils/deviceController';
import { getDeviceStatus } from '../../../core/api/api';

// 4 selectable speed steps as wedges. Off (10) and full-On (100) are the two
// states of the centre button â€” 10 = off, 100 = on (as specified by the product).
export const FAN_STEPS = [
  { speed: 20, label: 'SP1', color: '#1976d2' },
  { speed: 40, label: 'SP2', color: '#00acc1' },
  { speed: 60, label: 'SP3', color: '#43a047' },
  { speed: 80, label: 'SP4', color: '#fb8c00' },
];
const OFF_SPEED = 10;
const ON_SPEED = 100;

// Geometry is in a fixed viewBox; the SVG scales to the `size` prop.
const VB = 264;
const C = VB / 2;
const R_OUT = 122;
const R_IN = 52;
const GAP_DEG = 2.4;
const SEG = 360 / FAN_STEPS.length;

const rad = (deg) => (deg - 90) * (Math.PI / 180);
const polar = (r, deg) => [C + r * Math.cos(rad(deg)), C + r * Math.sin(rad(deg))];

function wedgePath(i) {
  const start = i * SEG + GAP_DEG / 2;
  const end = (i + 1) * SEG - GAP_DEG / 2;
  const [x1, y1] = polar(R_OUT, start);
  const [x2, y2] = polar(R_OUT, end);
  const [x3, y3] = polar(R_IN, end);
  const [x4, y4] = polar(R_IN, start);
  const large = end - start > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${R_OUT} ${R_OUT} 0 ${large} 1 ${x2} ${y2} `
       + `L ${x3} ${y3} A ${R_IN} ${R_IN} 0 ${large} 0 ${x4} ${y4} Z`;
}

export default function FanDial({ device, size = 264, initialStatus = '' }) {
  const { userId } = useApp();
  const uno = device.UniqueNumber;
  const [status, setStatus] = useDeviceStatus(uno, initialStatus);
  const [speed, setSpeed] = useState(OFF_SPEED); // 10 = off

  // Seed the last-known status from the DB when none was provided (inline card).
  // Only fill in if no live value has arrived yet, so MQTT is never clobbered.
  useEffect(() => {
    if (initialStatus) return undefined;
    let alive = true;
    (async () => {
      const st = await getDeviceStatus(uno).catch(() => null);
      if (!alive) return;
      const row = st && Array.isArray(st.data) ? st.data[0] : null;
      if (row && row.Status) {
        const db = parseStatus(row.Status);
        if (db) setStatus((cur) => cur || db);
      }
    })();
    return () => { alive = false; };
  }, [uno, initialStatus, setStatus]);

  useEffect(() => {
    if (!status) return;
    const parts = status.split('-');
    const s = Number(parts[parts.length - 1]);
    if (!Number.isNaN(s)) setSpeed(s);
  }, [status]);

  const sendSpeed = (s) => {
    setSpeed(s);
    publishCommand(uno, `1-${s}`, userId);
  };
  // 10 = off, 100 = on: the centre button flips between them.
  const on = speed > OFF_SPEED;
  const active = FAN_STEPS.find((st) => st.speed === speed);
  const centerLabel = !on ? 'OFF' : (active ? active.label : (speed >= ON_SPEED ? 'FULL' : String(speed)));
  const scale = size / VB;
  const centerD = (R_IN * 2 - 14) * scale;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, width: '100%' }}>
      <Box sx={{ position: 'relative', width: size, height: size, maxWidth: '100%' }}>
        <svg width="100%" height="100%" viewBox={`0 0 ${VB} ${VB}`}>
          {FAN_STEPS.map((st, i) => {
            const isActive = on && st.speed === speed;
            const [lx, ly] = polar((R_OUT + R_IN) / 2, i * SEG + SEG / 2);
            return (
              <g key={st.speed} onClick={() => sendSpeed(st.speed)} style={{ cursor: 'pointer' }}>
                <path
                  d={wedgePath(i)}
                  fill={st.color}
                  fillOpacity={isActive ? 1 : 0.32}
                  stroke={isActive ? '#fff' : 'transparent'}
                  strokeWidth={isActive ? 3 : 0}
                />
                <text
                  x={lx} y={ly}
                  textAnchor="middle" dominantBaseline="central"
                  fontSize="14" fontWeight="700"
                  fill="#fff" fillOpacity={isActive ? 1 : 0.85}
                  style={{ pointerEvents: 'none', userSelect: 'none' }}
                >
                  {st.label}
                </text>
              </g>
            );
          })}
        </svg>

        {/* Centre = state display only (not a toggle). */}
        <Box
          sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: centerD, height: centerD, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            userSelect: 'none',
            bgcolor: on ? 'success.main' : 'action.disabledBackground',
            color: on ? '#fff' : 'text.secondary',
            boxShadow: 2,
          }}
        >
          <Typography variant="caption" fontWeight={700}>{centerLabel}</Typography>
        </Box>
      </Box>

      {/* Separate On / Off buttons â€” On always sets full, never toggles off. */}
      <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
        <Button
          size="small" fullWidth
          variant={!on ? 'contained' : 'outlined'} color={!on ? 'error' : 'inherit'}
          startIcon={<PowerSettingsNewIcon />} onClick={() => sendSpeed(OFF_SPEED)}
          sx={{ color: !on ? undefined : 'text.primary', borderColor: !on ? undefined : 'divider' }}
        >
          Off
        </Button>
        <Button
          size="small" fullWidth
          variant={speed >= ON_SPEED ? 'contained' : 'outlined'} color={speed >= ON_SPEED ? 'success' : 'inherit'}
          startIcon={<PowerSettingsNewIcon />} onClick={() => sendSpeed(ON_SPEED)}
          sx={{ color: speed >= ON_SPEED ? undefined : 'text.primary', borderColor: speed >= ON_SPEED ? undefined : 'divider' }}
        >
          On
        </Button>
      </Box>
    </Box>
  );
}
